import React, { useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Play,
  Youtube,
  Eye,
  Clock,
  Mic,
  Search,
  ExternalLink,
  Sparkles,
  ChevronRight,
  CalendarDays,
} from 'lucide-react';
import PageLayout from '../components/layout/PageLayout';
import { Badge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import { api } from '../services/api';
import { cn } from '../lib/utils';

const NAV_LINKS = [
  { label: 'Sobre Nós', to: '/sobre' },
  { label: 'Pregações', to: '/pregações' },
  { label: 'Células', to: '/celulas' },
  { label: 'Eventos', to: '/evento' },
  { label: 'Galeria', to: '/galeria' },
  { label: 'Contato', to: '/contato' },
];

const FOOTER_COLUMNS = [
  {
    title: 'Navegação',
    links: [
      ['Em que Cremos', '/sobre'],
      ['História', '/sobre'],
      ['Eventos', '/evento'],
      ['Células', '/celulas'],
      ['Galeria', '/galeria'],
    ],
  },
  {
    title: 'Institucional',
    links: [
      ['Política de Privacidade', '/privacidade'],
      ['Relatórios', '/relatorios'],
      ['Doações', '/missoes'],
      ['Área Administrativa', '/admin'],
    ],
  },
];

const HERO =
  'https://images.unsplash.com/photo-1517456793572-1d8efd6dc135?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600';

const CHANNEL_COLORS = {
  caratinga: {
    badge: 'bg-primary-light text-primary',
    dot: 'bg-primary',
    ring: 'ring-primary/20',
    text: 'text-primary',
  },
  inhapim: {
    badge: 'bg-accent-light text-accent-dark',
    dot: 'bg-accent',
    ring: 'ring-accent/20',
    text: 'text-accent-dark',
  },
};

const FALLBACK_COLORS = [
  { badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', ring: 'ring-emerald-500/20', text: 'text-emerald-700' },
  { badge: 'bg-violet-100 text-violet-700', dot: 'bg-violet-500', ring: 'ring-violet-500/20', text: 'text-violet-700' },
  { badge: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500', ring: 'ring-rose-500/20', text: 'text-rose-700' },
  { badge: 'bg-sky-100 text-sky-700', dot: 'bg-sky-500', ring: 'ring-sky-500/20', text: 'text-sky-700' },
];

function channelColors(id, index = 0) {
  return CHANNEL_COLORS[id] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

const thumb = (videoId) => `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
const watchUrl = (videoId) => `https://www.youtube.com/watch?v=${videoId}`;

function HeroSection() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO} alt="Pregações da Comunidade Águas Vivas" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-dark/85 via-primary-dark/70 to-primary-dark/90" />
      </div>

      <div className="app-container relative py-16 md:py-24">
        <div className="max-w-2xl">
          <Badge tone="accent" className="mb-4">
            <Youtube size={12} /> Pregações no YouTube
          </Badge>
          <h1 className="font-heading text-4xl font-extrabold leading-tight text-white md:text-5xl">
            Pregações
          </h1>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/85 md:text-base">
            Assista às mensagens e cultos das comunidades de Caratinga e Inhapim,
            disponibilizados em nossos canais oficiais no YouTube.
          </p>
        </div>
      </div>
    </section>
  );
}

function ChannelCard({ channel, count, index }) {
  const colors = channelColors(channel.id, index);

  return (
    <a
      href={channel.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
      data-testid={`channel-${channel.id}`}
    >
      <div className="flex items-center gap-3">
        <span className={cn('flex h-12 w-12 items-center justify-center rounded-2xl', colors.badge)}>
          <Youtube size={22} />
        </span>
        <div className="min-w-0">
          <h3 className="font-heading text-lg font-bold text-ink">{channel.name}</h3>
          <p className="truncate text-xs font-medium text-ink-faint">{channel.handle}</p>
        </div>
      </div>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-soft">{channel.description}</p>
      <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-ink-soft">
          <Play size={13} className={colors.text} /> {count} pregações
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-primary-light px-3 py-1.5 font-heading text-xs font-bold text-primary transition-colors group-hover:bg-primary group-hover:text-white">
          Ver canal <ExternalLink size={12} />
        </span>
      </div>
    </a>
  );
}

function PreachingCard({ preaching, onPlay, active, channelName }) {
  const colors = channelColors(preaching.channelId);

  return (
    <article
      className={cn(
        'group flex flex-col overflow-hidden rounded-2xl border bg-white shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift',
        active ? 'border-primary ring-2 ring-primary/20' : 'border-slate-200'
      )}
      data-testid={`preaching-${preaching.id}`}
    >
      <button
        onClick={() => onPlay(preaching)}
        className="relative aspect-video w-full overflow-hidden text-left"
        aria-label={`Assistir: ${preaching.title}`}
      >
        <img
          src={thumb(preaching.videoId)}
          alt={preaching.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <span className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-black/60 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur">
          <Clock size={11} /> {preaching.duration}
        </span>
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 text-primary shadow-lift transition-transform duration-300 group-hover:scale-110">
            <Play size={24} className="ml-0.5" fill="currentColor" />
          </span>
        </span>
      </button>

      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-center gap-2">
          <Badge tone="primary">{channelName || 'Canal'}</Badge>
          <span className="text-[11px] font-medium text-ink-faint">{preaching.series}</span>
        </div>

        <h3 className="mt-2 line-clamp-2 font-heading text-sm font-bold leading-snug text-ink transition-colors group-hover:text-primary">
          {preaching.title}
        </h3>

        <p className="mt-2 flex items-center gap-1.5 text-xs text-ink-soft">
          <Mic size={13} className={colors.text} /> {preaching.preacher}
        </p>

        <div className="mt-3 flex items-center gap-4 border-t border-slate-100 pt-3 text-xs text-ink-faint">
          <span className="flex items-center gap-1">
            <CalendarDays size={13} />
            {format(parseISO(preaching.date), "dd 'de' MMM", { locale: ptBR })}
          </span>
          <span className="flex items-center gap-1">
            <Eye size={13} /> {preaching.views.toLocaleString('pt-BR')}
          </span>
        </div>
      </div>
    </article>
  );
}

function PlayerModal({ preaching, onClose, channelName }) {
  if (!preaching) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="preaching-player"
    >
      <div
        className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-lift"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <div className="min-w-0">
            <p className="truncate font-heading text-sm font-bold text-ink">{preaching.title}</p>
            <p className="text-xs text-ink-soft">
              {preaching.preacher} · {channelName || 'Canal'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-ink-soft transition-colors hover:bg-slate-200 hover:text-ink"
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>

        <div className="aspect-video w-full bg-black">
          <iframe
            className="h-full w-full"
            src={`https://www.youtube.com/embed/${preaching.videoId}?autoplay=1`}
            title={preaching.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-4">
          <p className="line-clamp-2 flex-1 text-sm text-ink-soft">{preaching.description}</p>
          <a
            href={watchUrl(preaching.videoId)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-red-600 px-4 py-2 font-heading text-xs font-bold text-white transition-colors hover:bg-red-700"
          >
            <Youtube size={14} /> Assistir no YouTube
          </a>
        </div>
      </div>
    </div>
  );
}

export default function Pregações() {
  const [channels, setChannels] = useState([]);
  const [preachings, setPreachings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeChannel, setActiveChannel] = useState('todas');
  const [query, setQuery] = useState('');
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    Promise.all([api.getPreachingChannels(), api.getPreachings()]).then(([ch, pr]) => {
      setChannels(ch);
      setPreachings(pr);
      setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => {
    return preachings
      .filter((p) => {
        const matchChannel = activeChannel === 'todas' || p.channelId === activeChannel;
        const matchQuery =
          !query ||
          p.title.toLowerCase().includes(query.toLowerCase()) ||
          p.preacher.toLowerCase().includes(query.toLowerCase()) ||
          p.series.toLowerCase().includes(query.toLowerCase());
        return matchChannel && matchQuery;
      })
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [preachings, activeChannel, query]);


  const countByChannel = useMemo(() => {
    const counts = {};
    preachings.forEach((p) => {
      counts[p.channelId] = (counts[p.channelId] || 0) + 1;
    });
    return counts;
  }, [preachings]);

  const channelById = useMemo(() => {
    const map = {};
    channels.forEach((ch) => {
      map[ch.id] = ch;
    });
    return map;
  }, [channels]);

  return (
    <PageLayout navLinks={NAV_LINKS} footerColumns={FOOTER_COLUMNS}>
      <HeroSection />

      {/* Canais */}
      <section className="app-container -mt-8 relative z-10">
        <div className="grid gap-4 sm:grid-cols-2">
          {channels.map((ch, i) => (
            <ChannelCard key={ch.id} channel={ch} count={countByChannel[ch.id] || 0} index={i} />
          ))}
        </div>
      </section>

      {/* Lista de pregações */}
      <section className="app-container mt-10">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="font-heading text-xl font-extrabold text-ink md:text-2xl">
              Todas as pregações
            </h2>
            <p className="mt-1 text-sm text-ink-soft">
              Filtre por comunidade ou busque por título, pregador ou série.
            </p>
          </div>

          <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white p-1.5 shadow-soft md:max-w-xs">
            <Search size={16} className="ml-2 shrink-0 text-ink-faint" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar pregação..."
              className="w-full bg-transparent py-1.5 text-sm text-ink outline-none placeholder:text-ink-faint"
              data-testid="preachings-search"
            />
          </div>
        </div>

        <div className="mt-5 flex gap-2 overflow-x-auto pb-2" data-testid="preachings-channels">
          <button
            onClick={() => setActiveChannel('todas')}
            data-testid="preachings-filter-todas"
            className={cn(
              'shrink-0 rounded-full px-4 py-2 font-heading text-sm font-semibold transition-colors duration-200',
              activeChannel === 'todas'
                ? 'bg-primary text-white shadow-soft'
                : 'border border-slate-200 bg-white text-ink-soft hover:text-primary'
            )}
          >
            Todas
          </button>
          {channels.map((ch) => (
            <button
              key={ch.id}
              onClick={() => setActiveChannel(ch.id)}
              data-testid={`preachings-filter-${ch.id}`}
              className={cn(
                'shrink-0 rounded-full px-4 py-2 font-heading text-sm font-semibold transition-colors duration-200',
                activeChannel === ch.id
                  ? 'bg-primary text-white shadow-soft'
                  : 'border border-slate-200 bg-white text-ink-soft hover:text-primary'
              )}
            >
              {ch.name}
            </button>
          ))}
        </div>

        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-soft">
                <Skeleton className="aspect-video w-full" />
                <div className="p-4">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="mt-3 h-5 w-full" />
                  <Skeleton className="mt-2 h-4 w-4/5" />
                </div>
              </div>
            ))
          ) : filtered.length === 0 ? (
            <div className="col-span-full rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
              <Sparkles size={32} className="mx-auto text-ink-faint" />
              <p className="mt-3 font-heading font-bold text-ink">Nenhuma pregação encontrada</p>
              <p className="mt-1 text-sm text-ink-soft">Tente ajustar a busca ou o filtro.</p>
            </div>
          ) : (
            filtered.map((p) => (
              <PreachingCard
                key={p.id}
                preaching={p}
                onPlay={setPlaying}
                active={playing?.id === p.id}
                channelName={channelById[p.channelId]?.name}
              />
            ))
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="app-container mt-14">
        <div className="relative overflow-hidden rounded-2xl bg-primary-dark p-6 text-white shadow-lift md:p-10">
          <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/10" />
          <div className="absolute -bottom-10 -left-6 h-32 w-32 rounded-full bg-accent/20" />
          <div className="relative flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/15">
                <Youtube size={24} />
              </span>
              <h3 className="mt-4 font-heading text-xl font-extrabold md:text-2xl">
                Inscreva-se nos nossos canais
              </h3>
              <p className="mt-2 max-w-md text-sm text-white/80">
                Acompanhe as transmissões ao vivo e receba as novas pregações das comunidades
                de Caratinga e Inhapim.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              {channels.map((ch) => (
                <a
                  key={ch.id}
                  href={ch.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`channel-cta-${ch.id}`}
                  className="inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-6 py-3 font-heading text-sm font-bold text-primary transition-transform duration-200 hover:-translate-y-0.5"
                >
                  <Youtube size={16} className="text-red-600" /> {ch.name}
                  <ChevronRight size={14} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <PlayerModal
        preaching={playing}
        onClose={() => setPlaying(null)}
        channelName={playing ? channelById[playing.channelId]?.name : undefined}
      />
    </PageLayout>
  );
}
